import { useTranslation } from 'react-i18next';

/**
 * PMO KPI 画面の表示 view。
 * `process` = 案件が工程をどう流れているか（充足率・フェーズ別リードタイム）、
 * `delivery` = 約束どおり出せているか（遅延・リリース数）。
 * @graph-connects none
 */
export type PmoView = 'process' | 'delivery';

export interface PmoViewSwitchProps {
  /** 現在の表示 view */
  view: PmoView;
  /** view 変更コールバック */
  onChange: (next: PmoView) => void;
}

/**
 * プロセス（青）/ デリバリー（琥珀）の view を切替える pill スイッチ。
 *
 * 色は意味に紐づける。プロセスは工程の流れ＝中立の情報なのでアクセント青、
 * デリバリーは遅延の監視が主目的なので警告寄りの琥珀にする。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PMO KPI画面のプロセス/デリバリー view 切替 pill スイッチ（テキストのみ・アイコンなし、切替時にアクティブ色がグラデーション遷移）
 * @graph-connects none
 */
export function PmoViewSwitch({ view, onChange }: PmoViewSwitchProps) {
  const { t } = useTranslation();

  return (
    <div
      className="inline-flex items-center gap-0.5 rounded-full border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-0.5"
      role="group"
      aria-label={t('kpi.pmoView.switchLabel')}
    >
      <button
        type="button"
        aria-pressed={view === 'process'}
        onClick={() => {
          onChange('process');
        }}
        className={`rounded-full px-4 py-1 text-xs font-semibold transition-colors duration-300 ${
          view === 'process' ? 'bg-blue-500 text-white' : 'text-blue-600 hover:text-blue-700'
        }`}
      >
        {t('kpi.pmoView.process')}
      </button>
      <button
        type="button"
        aria-pressed={view === 'delivery'}
        onClick={() => {
          onChange('delivery');
        }}
        className={`rounded-full px-4 py-1 text-xs font-semibold transition-colors duration-300 ${
          view === 'delivery' ? 'bg-amber-500 text-white' : 'text-amber-600 hover:text-amber-700'
        }`}
      >
        {t('kpi.pmoView.delivery')}
      </button>
    </div>
  );
}
